import { useTranslation } from 'react-i18next';
import { Badge, Box, Button, FileButton, Group, Stack, Text } from '@mantine/core';
import { IconSignature } from '@tabler/icons-react';
import DocRow from '@/components/app/deals/steps/forwarding/DocRow';
import { useAvailableDocTypes, useUploadSignedDailyUpd } from '@/hooks/useDeals';
import { collectDailyUpds } from '@/lib/forwardingView';
import type { BargainDocument, BargainStepsInfo, SignatureRole } from '@/types/deals';
import { formatOrgName } from '@/lib/format';
import IdText from '@/components/common/IdText';

interface DailyUpdSigningProps {
  readonly info: BargainStepsInfo;
  /** Передаётся в живом шаге → доступно действие подписи (симуляция ЭДО). В read-only отсутствует. */
  readonly dealId?: number;
}

const SIGNED_DAILY_UPD = 'SIGNED_DAILY_UPD';
const SIGN_ROLES: readonly SignatureRole[] = ['SELLER', 'BUYER', 'AGENT', 'FORWARDER', 'BANK'];

// Секция «Подпись УПД»: дневные УПД по дням отгрузки со статусами подписи (Покупатель/Агент) и
// скачиванием; реквизиты стороны-эмитента (продавец) и общий статус подписания. В живом шаге, когда
// бэк разрешает загрузку SIGNED_DAILY_UPD, доступна загрузка подписанного УПД — симуляция ЭДО на dev.
export default function DailyUpdSigning({ info, dealId }: DailyUpdSigningProps) {
  const { t } = useTranslation();
  // УПД — документ приёмной стороны по дню (из реестра принятого), подписывается через ЭДО. Берём из
  // общего списка документов сделки. Неподписанные (ждут чьей-либо подписи) — сверху, затем по дате.
  const isUnsigned = (doc: BargainDocument): boolean =>
    SIGN_ROLES.some((role) => doc.signatures?.[role]?.state === 'WAITED');
  const upds = [...collectDailyUpds(info.documents)].sort((a, b) => {
    const byPending = Number(isUnsigned(b)) - Number(isUnsigned(a));
    return byPending !== 0 ? byPending : (a.deliveryDate ?? '').localeCompare(b.deliveryDate ?? '');
  });
  const availableTypes = useAvailableDocTypes(dealId ?? null);
  const upload = useUploadSignedDailyUpd(dealId ?? -1);

  if (upds.length === 0) {
    return null;
  }

  const waiting = upds.some((doc) => SIGN_ROLES.some((role) => doc.signatures?.[role]?.state === 'WAITED'));
  const seller = info.seller;
  const canSign =
    dealId != null && (availableTypes.data ?? []).some((item) => item.documentType === SIGNED_DAILY_UPD);

  return (
    <Stack gap="sm">
      <Group justify="space-between" wrap="nowrap">
        <Text fz="sm" fw={600} c="gray.8">
          {t('app.deals.forwarding.updTitle')}
        </Text>
        <Badge variant="light" color={waiting ? 'wheat' : 'lime'} tt="none" fw={500} radius="sm">
          {t(waiting ? 'app.deals.forwarding.updStatusWaiting' : 'app.deals.forwarding.updStatusDone')}
        </Badge>
      </Group>

      {seller?.name ? (
        <Box>
          <Text fz="xs" c="gray.7" mb={2}>
            {t('app.deals.summary.seller')}
          </Text>
          <Text fz="sm" fw={500}>
            {formatOrgName(seller.name)}
          </Text>
          <Group gap="md" wrap="wrap">
            {seller.inn ? (
              <Text fz="sm" c="gray.7">
                {t('auth.fields.inn')} <IdText>{seller.inn}</IdText>
              </Text>
            ) : null}
            {seller.ogrn ? (
              <Text fz="sm" c="gray.7">
                {t('app.company.card.ogrn')} <IdText>{seller.ogrn}</IdText>
              </Text>
            ) : null}
            {seller.okpo ? (
              <Text fz="sm" c="gray.7">
                {t('app.company.card.okpo')} <IdText>{seller.okpo}</IdText>
              </Text>
            ) : null}
          </Group>
        </Box>
      ) : null}

      <Stack gap="xs">
        {upds.map((doc, i) => (
          <DocRow key={doc.id ?? i} doc={doc} withSignatures />
        ))}
      </Stack>

      {canSign ? (
        <Group gap="sm" wrap="wrap" align="center">
          <FileButton onChange={(file) => file && upload.mutate(file)} accept=".pdf,.xlsx,.xls,application/pdf">
            {(props) => (
              <Button
                {...props}
                variant="light"
                leftSection={<IconSignature size={16} />}
                loading={upload.isPending}
                w="fit-content"
              >
                {t('app.deals.forwarding.updSign')}
              </Button>
            )}
          </FileButton>
          <Text fz="xs" c="gray.6">
            {t('app.deals.forwarding.updSignHint')}
          </Text>
        </Group>
      ) : null}
    </Stack>
  );
}
